import React, { useCallback } from 'react';
import { message, Modal, Switch } from 'antd';
import { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import Banner from './banner.jpg';
import commonS from 'styles/common.module.less';
import { TKlarityServiceServerTableItem } from 'types/common';
import CommonTable from 'components/CommonTable';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import { disableKlarityService } from 'api/provider';

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    prescriptionServiceList: TKlarityServiceServerTableItem[];
};

const PrescriptionConfigModal = (props: Props) => {
    const { open, onClose, prescriptionServiceList } = props;
    const navigate = useNavigate();
    // onConfirm 将在后续实现确认功能时使用
    const [stateServiceListMap, setStateServiceListMap] = React.useState<
        Record<string, TKlarityServiceServerTableItem[]>
    >({});
    // 管理每个服务的 toggle 状态，key 为 itemid
    const [serviceEnabledMap, setServiceEnabledMap] = React.useState<Record<number, boolean>>({});
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (prescriptionServiceList && prescriptionServiceList.length > 0) {
            const _stateServiceListMap: Record<string, TKlarityServiceServerTableItem[]> = {};
            const _serviceEnabledMap: Record<number, boolean> = {};
            prescriptionServiceList.forEach((item) => {
                _stateServiceListMap[item.state] = [...(_stateServiceListMap[item.state] || []), item];
                // 默认所有服务都是启用状态
                _serviceEnabledMap[item.itemId] = true;
            });
            setStateServiceListMap(_stateServiceListMap);
            setServiceEnabledMap(_serviceEnabledMap);
        }
    }, [prescriptionServiceList]);

    const onOptOut = () => {
        prescriptionServiceList.forEach((item) => {
            setServiceEnabledMap((prev) => ({
                ...prev,
                [item.itemId]: false,
            }));
        });
    };

    // 处理 toggle switch 的变更
    const handleToggleChange = useCallback((itemId: number, checked: boolean) => {
        setServiceEnabledMap((prev) => ({
            ...prev,
            [itemId]: checked,
        }));
    }, []);

    const onConfirm = async () => {
        const disableIds = Object.keys(serviceEnabledMap).filter((key) => !serviceEnabledMap[Number(key)]);
        if (disableIds.length > 0) {
            setLoading(true);
            const res = await disableKlarityService(disableIds.map((key) => Number(key)));
            setLoading(false);
            if (!res.error) {
                message.success('Service disabled successfully');
                onClose?.();
            }
        } else {
            onClose?.();
        }
    };

    // 为每个州生成表格列配置
    const getColumns = useCallback(
        (state: string, services: TKlarityServiceServerTableItem[]): ColumnsType<TKlarityServiceServerTableItem> => {
            return [
                {
                    title: <div className={s.tableTitle}>STATES</div>,
                    dataIndex: 'state',
                    key: 'state',
                    width: 100,
                    render: (val: string, record, index) => {
                        // 只在第一行显示州名，并设置 rowSpan
                        if (index === 0) {
                            return {
                                children: <div className={s.stateCell}>{state}</div>,
                                props: {
                                    rowSpan: services.length,
                                },
                            };
                        }
                        return {
                            children: null,
                            props: {
                                rowSpan: 0,
                            },
                        };
                    },
                },
                {
                    title: <div className={s.tableTitle}>SPECIALTY</div>,
                    dataIndex: 'serviceSpecialtyName',
                    key: 'serviceSpecialtyName',
                    render: (val: string, item: TKlarityServiceServerTableItem) => {
                        return (
                            <div className={s.specialtyCell}>
                                {val}
                                {item.customizedServiceName ? ` [${item.customizedServiceName}]` : ''}
                            </div>
                        );
                    },
                },
                {
                    title: <div className={s.tableTitle} />,
                    key: 'toggle',
                    width: 80,
                    render: (_, record) => {
                        return (
                            <div className={s.toggleCell}>
                                <Switch
                                    checked={serviceEnabledMap[record.itemId]}
                                    onChange={(checked) => handleToggleChange(record.itemId, checked)}
                                />
                            </div>
                        );
                    },
                },
            ];
        },
        [serviceEnabledMap, handleToggleChange],
    );

    return (
        <Modal
            wrapClassName={s.modalWrap}
            className={commonS.modalFixHeightWrap}
            width={700}
            open={open}
            closeIcon={null}
            okText="Confirm settings"
            cancelText="Review settings in Service templates"
            onCancel={() => {
                navigate(`/${PATH.PPA_SERVICE_TEMPLATE}?from=menu`);
                onClose?.();
            }}
            onOk={onConfirm}
            okButtonProps={{
                loading,
            }}
        >
            <div className={s.wrap}>
                <img src={Banner} alt="banner" className={s.banner} />
                <div className={s.content}>
                    <div className={s.title}>
                        All <b>prescription refills</b> are now handled via asynchronous text visit
                    </div>
                    <div className={s.des}>
                        <p>
                            Prescription refill requests are now supported through <b>asynchronous text visits only</b>,
                            without scheduling a video or in-person appointment.
                        </p>
                        <p>
                            Review your settings below. If you prefer not to offer prescription refills via text visits,
                            you can opt out and continue prescribing through follow-up visits instead.
                        </p>
                    </div>
                    <div className={s.out} onClick={onOptOut}>
                        Opt out all prescription services
                    </div>
                    <div className={s.tableWrap}>
                        {Object.keys(stateServiceListMap).map((state) => {
                            const services = stateServiceListMap[state];
                            if (!services || services.length === 0) return null;

                            return (
                                <div key={state} className={s.stateTableWrap}>
                                    <CommonTable
                                        bordered
                                        rowKey="itemId"
                                        columns={getColumns(state, services)}
                                        data={services}
                                        pagination={false}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PrescriptionConfigModal;
